import Button from '@/app/components/common/Button'
import HotelListItemSmall from '@/app/components/HotelListItemSmall'
import { Icons } from '@/app/components/ui/Icons'
import { SM_BREAKPOINT, XL_BREAKPOINT } from '@/app/constants'
import eventBus from '@/app/eventBus'
import { PS_EVENTS } from '@/app/ps-events'
import { HotelItem, HotelsListResponse } from '@/app/types'
import { getBoundsPolygonString } from '@/app/utils'
import { useMediaQuery } from '@uidotdev/usehooks'
import {
	AdvancedMarker,
	APIProvider,
	InfoWindow,
	Map,
	MapProps
} from '@vis.gl/react-google-maps'
import cn from 'classnames'
import { useTranslations } from 'next-intl'
import { Dispatch, useEffect, useState } from 'react'

interface HotelMapProps {
	mapProps?: MapProps,
	fullMapView?: boolean,
	mapCenter: { lat: number, lng: number },
	isCity?: boolean,
	singleHotelResponse?: HotelItem | null,
	isLandmark?: boolean,
	isLoading?: boolean,
	isFallbackData?: boolean,
	hoveredItem: string | number | null,
	onMapChange: Dispatch<React.SetStateAction<string>>,
	lang?: string,
	searchParams: HotelsListResponse['searchParams'],
	hotels: HotelItem[],
	onMarkerSelect?: (hotel: HotelItem) => void
}

const LoadingIndicator = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="46" height="10" viewBox="0 0 46 10" fill="none">
		<circle opacity="0.3" cx="5" cy="5" r="5" fill="#CBD5E1" />
		<circle opacity="0.6" cx="23" cy="5" r="5" fill="#CBD5E1" />
		<circle cx="41" cy="5" r="5" fill="#CBD5E1" />
	</svg>
)

export const HotelsListMap = ({
	mapProps,
	fullMapView,
	isLandmark,
	isCity,
	mapCenter,
	singleHotelResponse,
	isLoading,
	isFallbackData,
	onMapChange,
	searchParams,
	hoveredItem,
	hotels, lang,
}: HotelMapProps) => {
	const t = useTranslations('Keywords')
	const isDesktop = useMediaQuery(XL_BREAKPOINT)
	const isMobile = useMediaQuery(SM_BREAKPOINT)
	const [infoWindowOpen, setInfoWindowOpen] = useState(false)
	const [initialCallDone, setInitialCallDone] = useState(false)
	const [selectedHotel, setSelectedHotel] = useState<null | HotelItem>()
	const url = new URL(window.location.href)
	const currentSearchParams = new URLSearchParams(url.search)
	const zoom = currentSearchParams.get('zoom')
	const polygon = currentSearchParams.get('polygon')
	const defaultMapCenter = singleHotelResponse ? { lat: singleHotelResponse.rawHotelData.lat || 0, lng: singleHotelResponse.rawHotelData.long || 0 } : mapCenter
	const handleCloseButtonClick = () => {
		if (isDesktop) {
			if (fullMapView) {
				eventBus.publish(PS_EVENTS.FULL_SCREEN_MAP, false)
			}
			eventBus.publish(PS_EVENTS.LIST_VIEW, true)
		} else {
			eventBus.publish(PS_EVENTS.FULL_SCREEN_MAP, !fullMapView)
		}
	}

	const handleFullScreenMapClick = () => {
		if (isDesktop) {
			eventBus.publish(PS_EVENTS.FULL_SCREEN_MAP, !fullMapView)
		}
	}

	useEffect(() => {
		if (isLoading) {
			handleCloseMarker()
		}
	}, [isLoading])

	const handleMarkerClick = (hotel: HotelItem) => {
		setInfoWindowOpen(true)
		setSelectedHotel(hotel)
	}

	const handleCloseMarker = () => {
		setInfoWindowOpen(false)
		setSelectedHotel(null)
	}

	// Handle both dragend and zoom change events
	const handleViewportChanged = (e?: any) => {
		// if (infoWindowOpen) handleCloseMarker()
		if (singleHotelResponse && initialCallDone) return;
		// Prefer event.map if provided by the library; fallback to useMap()
		// const mapInstance = e?.map || map
		const mapInstance = e?.map
		const polygonString = getBoundsPolygonString(mapInstance, 3)

		if (!mapInstance || !polygonString) return

		// Get center coordinates
		const center = mapInstance.getCenter()
		if (!center) return

		const newLat = center.lat().toFixed(6)
		const newLng = center.lng().toFixed(6)

		// Update URL with new polygon, lat, and lng parameters
		const currentUrl = new URL(window.location.href)
		const newSearchParams = new URLSearchParams(currentUrl.search)

		newSearchParams.set('zoom', e.map.zoom)
		newSearchParams.set('lat', newLat)
		newSearchParams.set('lng', newLng)
		// Define polygon and reset page only when user manually changes the map
		if (initialCallDone) {
			newSearchParams.set('polygon', polygonString)
			if (polygonString != polygon) {
				newSearchParams.set('page', '1')
			}
		}

		const newUrl = `${currentUrl.pathname}?${newSearchParams.toString()}`
		window.history.replaceState(null, '', newUrl)
		if (initialCallDone) onMapChange(polygonString)
		if (!initialCallDone) setInitialCallDone(true)
	}

	return (
		<div className={cn("hotel-list-map",
			{
				'full-map-view': fullMapView,
				'full-screen-map-view': singleHotelResponse && fullMapView && isDesktop
			})}>
			<Button
				name="mapViewClose"
				textColor="text-900"
				fontSize="xl"
				bgColor="white"
				onClick={handleCloseButtonClick}
				bold
				className="map-button close-full-map-view-button"
			>
				<span className='max-xl:hidden'>{t('close')}</span>
				<Icons.close className='[&_path]:stroke-text-900 w-6 h-6 xl:w-5 xl:h-5' />
			</Button>
			{isLoading && (
				<div
					id="loading-indicator"
					className="map-button loading-indicator"
				>
					<LoadingIndicator />
				</div>
			)}
			{isDesktop && (
				<Button
					name="mapView"
					bgColor="white"
					onClick={handleFullScreenMapClick}
					className="map-button expand-view-button"
				>
					{fullMapView ?
						<Icons.minimize width="24" height="24" /> :
						<Icons.expand width="24" height="24" />}
				</Button>
			)}

			{/*  commented  not in use anymore for now
			{isFallbackData && fullMapView && isDesktop && (
				<div className="no-results-alert">
					<b>{t('resultsFound', { resultsCount: 0 })}</b>&nbsp;
					<span>{t('tryToReset')}</span>
				</div>
			)}
			 */}
			<APIProvider apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || ''}>
				<Map
					defaultCenter={defaultMapCenter}
					mapId="hotel-map"
					defaultZoom={zoom ? Number(zoom) : 14}
					disableDefaultUI={false}
					zoomControl={true}
					fullscreenControl={false}
					cameraControl={false}
					mapTypeControl={false}
					{...mapProps}
					onDragend={handleViewportChanged}
					onZoomChanged={handleViewportChanged}
					clickableIcons={false}
					streetViewControl={false}
					keyboardShortcuts={true}
					gestureHandling={'greedy'}
					onTilesLoaded={!initialCallDone ? handleViewportChanged : undefined}
				>
					{(isLandmark || isCity) && !singleHotelResponse && <AdvancedMarker position={mapCenter} clickable={false}>
						<div className='location-marker'>
							<Icons.flag width="24" height="24" fill="#1E293B" />
						</div>
					</AdvancedMarker>}
					{hotels.filter(i => !!i.rawPrice).map((item, index) => (
						<AdvancedMarker
							zIndex={hoveredItem === item.id || selectedHotel?.title === item.title ? 2 : 1}
							position={{ lat: item.rawHotelData.lat || 0, lng: item.rawHotelData.long || 0 }}
							key={item.title + index}
							onClick={() => handleMarkerClick(item)}
						>
							<div className={cn("marker",
								{
									selected: selectedHotel?.title === item.title,
									hovered: hoveredItem === item.id,
									fallbackHotel: isFallbackData
								})}
							>
								{!isFallbackData && (
									<>
										{item.price}
									</>
								)}
							</div>
						</AdvancedMarker>
					))}
					{singleHotelResponse && (
						<AdvancedMarker
							zIndex={3}
							position={{ lat: singleHotelResponse.rawHotelData.lat || 0, lng: singleHotelResponse.rawHotelData.long || 0 }}
							onClick={() => handleMarkerClick(singleHotelResponse)}
						>
							<div className={cn("marker selected", { noPrice: !singleHotelResponse.rawPrice })}>
								{singleHotelResponse.rawPrice ? singleHotelResponse.price : null}
							</div>
						</AdvancedMarker>
					)}

					{infoWindowOpen && selectedHotel && !isMobile && (
						<InfoWindow
							className="hotel-list-map__info-window"
							position={{ lat: selectedHotel.rawHotelData.lat || 0, lng: selectedHotel.rawHotelData.long || 0 }}
							onCloseClick={handleCloseMarker}
							pixelOffset={[0, -35]}
						>
							<HotelListItemSmall
								index={selectedHotel.rawPrice}
								isMapCard
								fullMapView={fullMapView}
								item={selectedHotel}
								searchParams={searchParams}
								lang={lang}
							/>
						</InfoWindow>
					)}
				</Map>
			</APIProvider>
			{infoWindowOpen && selectedHotel && isMobile && (
				<div className='hotel-list-map__info-window--mobile'>
					<Button name="close-info-mobile" className='close-info-mobile' onClick={handleCloseMarker}>
						<Icons.close />
					</Button>
					<HotelListItemSmall
						index={selectedHotel.rawPrice}
						isMapCard
						fullMapView={fullMapView}
						item={selectedHotel}
						searchParams={searchParams}
						lang={lang}
					/>
				</div>
			)}
		</div>
	)
}